import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { VideoCamera, Image as ImageIcon, Plus, Trash, X, FilmSlate, ChatCircle } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable media gallery for tournaments | retas | court_rentals.
 * Shows a compact trigger button that opens a dialog with the full gallery + uploader.
 */
export default function EventMediaGallery({ entityType, entityId, compact = false, testIdPrefix }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const tid = testIdPrefix || `${entityType}-media`;

  const load = useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    api.get(`/media/${entityType}/${entityId}`)
      .then(({ data }) => setMedia(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isVid = f.type.startsWith("video/");
    if (!isImg && !isVid) { toast.error("Solo imagenes o videos"); return; }
    const max = isImg ? 10 : 60;
    if (f.size > max * 1024 * 1024) { toast.error(`Archivo muy grande (max ${max}MB)`); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post(`/media/${entityType}/${entityId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isVid ? "Video subido" : "Foto subida");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    try {
      await api.delete(`/media/${entityType}/${entityId}/${m.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const mediaUrl = (m) => `${API_URL}/api/media/${entityType}/${entityId}/${m.id}`;
  const canDelete = (m) => user && (user.role === "admin" || user.role === "directivo" || m.uploaded_by === user.id);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className={`border-white/10 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 text-xs uppercase font-bold ${compact ? "h-7 text-[10px]" : ""}`}
        data-testid={`${tid}-open-btn`}
      >
        <FilmSlate size={compact ? 12 : 14} className="mr-1" />
        Fotos/Videos
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${tid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Galeria del evento</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <p className="text-xs text-gray-400">{media.length} archivo{media.length !== 1 ? "s" : ""}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs"
                data-testid={`${tid}-upload-btn`}
              >
                <Plus size={14} className="mr-1" /> {uploading ? "Subiendo..." : "Agregar"}
              </Button>
              <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onFile} data-testid={`${tid}-file-input`} />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : media.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-white/10">
              <ImageIcon size={40} weight="duotone" className="text-gray-600 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Sin fotos ni videos aun</p>
              <p className="text-[10px] text-gray-600 mt-1">Imagenes hasta 10MB · Videos hasta 60MB</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${tid}-grid`}>
              {media.map((m) => (
                <div key={m.id} className="relative group bg-[#0A0A0A] border border-white/10 overflow-hidden aspect-square" data-testid={`${tid}-item-${m.id}`}>
                  {m.type === "video" ? (
                    <div onClick={() => setPreview(m)} className="relative w-full h-full flex items-center justify-center bg-black cursor-pointer">
                      <VideoCamera size={40} weight="duotone" className="text-[#CCFF00]" />
                      <span className="absolute bottom-1 left-1 bg-black/70 text-[9px] px-1.5 py-0.5 text-[#CCFF00] uppercase">video</span>
                    </div>
                  ) : (
                    <img
                      src={mediaUrl(m)}
                      alt={m.caption || "foto"}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setPreview(m)}
                      loading="lazy"
                    />
                  )}
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canDelete(m) && (
                      <button onClick={() => handleDelete(m)} className="bg-red-500/80 hover:bg-red-600 text-white p-1" data-testid={`${tid}-delete-${m.id}`}>
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                    <p className="text-[9px] text-gray-300 truncate">{m.uploaded_by_name || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-black border-white/10 max-w-4xl p-2 max-h-[95vh] overflow-y-auto" data-testid={`${tid}-preview`}>
          <button onClick={() => setPreview(null)} className="absolute top-2 right-2 z-10 bg-white/10 hover:bg-white/20 p-1.5 rounded-full"><X size={16} /></button>
          {preview?.type === "video" ? (
            <video src={mediaUrl(preview)} controls autoPlay className="w-full max-h-[70vh]" data-testid={`${tid}-preview-video`} />
          ) : preview ? (
            <img src={mediaUrl(preview)} alt="" className="w-full max-h-[70vh] object-contain" data-testid={`${tid}-preview-img`} />
          ) : null}
          {preview?.caption && <p className="text-xs text-gray-400 text-center mt-2">{preview.caption}</p>}
          {preview && (
            <MediaComments
              entityType={entityType}
              entityId={entityId}
              mediaId={preview.id}
              currentUser={user}
              tid={tid}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function MediaComments({ entityType, entityId, mediaId, currentUser, tid }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/media/${entityType}/${entityId}/${mediaId}/comments`)
      .then(({ data }) => setComments(data || []))
      .catch(() => {});
  }, [entityType, entityId, mediaId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/media/${entityType}/${entityId}/${mediaId}/comments`, { text: t });
      setText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  const del = async (cid) => {
    if (!window.confirm("¿Eliminar comentario?")) return;
    try {
      await api.delete(`/media/comments/${cid}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const canDelete = (c) => currentUser && (c.user_id === currentUser.id || currentUser.role === "admin" || currentUser.role === "directivo");

  return (
    <div className="mt-4 bg-[#0A0A0A] border border-white/10 p-3" data-testid={`${tid}-comments`}>
      <div className="flex items-center gap-2 mb-3">
        <ChatCircle size={14} className="text-[#CCFF00]" />
        <p className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-bold">Comentarios ({comments.length})</p>
      </div>
      <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3">
        {comments.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic">Sé el primero en comentar</p>
        ) : comments.map((c) => (
          <div key={c.id} className="flex items-start gap-2 text-xs bg-[#141414] border border-white/5 p-2" data-testid={`${tid}-comment-${c.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white text-[11px]">{c.user_name} <span className="text-gray-600 font-normal">· {c.user_role}</span></p>
              <p className="text-gray-300 whitespace-pre-wrap break-words">{c.text}</p>
            </div>
            {canDelete(c) && (
              <button onClick={() => del(c.id)} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`${tid}-delete-comment-${c.id}`}>
                <Trash size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder="Escribe un comentario..."
          maxLength={800}
          className="bg-[#141414] border-white/10 text-white text-xs h-8"
          data-testid={`${tid}-comment-input`}
        />
        <Button
          size="sm"
          onClick={submit}
          disabled={sending || !text.trim()}
          className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-[10px] h-8"
          data-testid={`${tid}-comment-submit`}
        >
          Enviar
        </Button>
      </div>
    </div>
  );
}
